import { Children, createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { ArrowLeft, ChevronDown, ChevronUp, ChevronsUpDown, MoreHorizontal, X } from 'lucide-react';
import { useDirty } from '../../lib/dirty';
import { withListQuery } from '../../lib/record-step';
import { downloadWorkbook, excelFileName, type ExcelValue } from '../../lib/excel';
import { PAGE_SIZES } from '../../lib/list-query';
import { enumLabel } from '../../lib/labels';
import { Select } from './Input';
import { useNotice } from './Notice';

export interface Crumb { label: string; to?: string }

interface ChromeApi {
  crumbs: Crumb[];
  setCrumbs: (crumbs: Crumb[]) => void;
}

const ChromeContext = createContext<ChromeApi | null>(null);

export function PageChromeProvider({ children }: { children: ReactNode }) {
  const [crumbs, setCrumbs] = useState<Crumb[]>([]);
  const value = useMemo(() => ({ crumbs, setCrumbs }), [crumbs]);
  return <ChromeContext.Provider value={value}>{children}</ChromeContext.Provider>;
}

export function usePageCrumbs(): Crumb[] {
  return useContext(ChromeContext)?.crumbs || [];
}

export function CrumbTrail({ crumbs }: { crumbs: Crumb[] }) {
  const navigate = useNavigate();
  const dirty = useDirty();
  return (
    <nav aria-label="Breadcrumb" className="flex min-w-0 items-center text-sm text-[var(--bo-faint)]">
      {crumbs.map((crumb, index) => (
        <span key={`${crumb.label}-${index}`} className="flex min-w-0 items-center">
          {index ? <span className="px-1.5">/</span> : null}
          {crumb.to && index < crumbs.length - 1 ? (
            <button type="button" className="truncate hover:text-[var(--bo-text)]" onClick={() => dirty.requestLeave(() => navigate(crumb.to!))}>{crumb.label}</button>
          ) : <span className="truncate">{crumb.label}</span>}
        </span>
      ))}
    </nav>
  );
}

export function ActionCluster({ children }: { children: ReactNode }) {
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const items = Children.toArray(children).filter(Boolean);
  useEffect(() => { setOpen(false); }, [location.pathname, location.search]);
  if (!items.length) return null;
  return (
    <div className={`bo-page-actions ${open ? 'bo-fab-open' : ''}`}>
      <div className="bo-fab-menu" onClick={(event) => { if ((event.target as HTMLElement).closest('button, a')) setOpen(false); }}>{children}</div>
      <button type="button" className="bo-fab-toggle inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-full border-0 bg-[#895CF6] p-0 text-white shadow-lg" aria-label={open ? 'Close actions' : 'Actions'} aria-expanded={open} onClick={() => setOpen((value) => !value)}>
        {open ? <X className="h-5 w-5" /> : <MoreHorizontal className="h-5 w-5" />}
      </button>
    </div>
  );
}

export function PageHead({ crumbs, back, title, subtitle, meta, actions, actionsInline = false, stepper }: {
  crumbs?: Crumb[];
  back?: { label: string; to: string };
  title: string;
  subtitle?: string;
  meta?: ReactNode;
  actions?: ReactNode;
  actionsInline?: boolean;
  stepper?: ReactNode;
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const dirty = useDirty();
  const arrived = (location.state as { back?: { label: string; to: string } } | null)?.back;
  const backLink = arrived?.label && arrived.to ? arrived : back;
  const backTo = backLink ? withListQuery(backLink.to, location.search) : '';
  const setCrumbs = useContext(ChromeContext)?.setCrumbs;
  const trail = JSON.stringify(crumbs || []);
  useEffect(() => {
    setCrumbs?.(JSON.parse(trail) as Crumb[]);
    return () => setCrumbs?.([]);
  }, [setCrumbs, trail]);
  return (
    <div className="mb-5 flex flex-col gap-3">
      {stepper}
      <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
      <div className="min-w-0">
        {backLink ? (
          <button type="button" className="mb-3 inline-flex items-center gap-1.5 text-sm text-[var(--bo-muted)] hover:text-[var(--bo-text)]" onClick={() => dirty.requestLeave(() => navigate(backTo))}>
            <ArrowLeft className="h-4 w-4" />
            {backLink.label}
          </button>
        ) : null}
        <h1 className="break-words text-[28px] font-semibold leading-tight tracking-tight">{title}</h1>
        {subtitle ? <p className="mt-1 text-sm text-[var(--bo-muted)]">{subtitle}</p> : null}
        {meta ? <div className="mt-2 flex flex-wrap items-center gap-2 text-sm text-[var(--bo-muted)]">{meta}</div> : null}
      </div>
      {actions ? (actionsInline ? <div className="flex flex-shrink-0 flex-wrap items-center gap-2">{actions}</div> : <ActionCluster>{actions}</ActionCluster>) : null}
      </div>
    </div>
  );
}

export function RecordLink({ to, backLabel, backTo, children, className, onClick }: {
  to: string;
  backLabel: string;
  backTo?: string;
  children: ReactNode;
  className?: string;
  onClick?: () => void;
}) {
  const location = useLocation();
  return (
    <Link className={className || 'text-xs text-[var(--bo-link)]'} to={to} state={{ back: { label: backLabel, to: backTo || `${location.pathname}${location.search}` } }} onClick={onClick}>
      {children}
    </Link>
  );
}

export function FilterTabs({ tabs, active, onChange }: {
  tabs: { id: string; label: string; count?: number }[];
  active: string;
  onChange: (id: string) => void;
}) {
  return (
    <div className="bo-filter-row mb-4 flex gap-1 overflow-x-auto overscroll-x-contain border-b border-[var(--bo-border)]">
      {tabs.map((tab) => {
        const on = tab.id === active;
        return (
          <button key={tab.id} type="button" className={`shrink-0 whitespace-nowrap border-b-2 px-3 py-2 text-sm ${on ? 'border-[#895CF6] text-[var(--bo-text)]' : 'border-transparent text-[var(--bo-muted)]'}`} onClick={() => onChange(tab.id)}>
            {tab.label}
            {on && tab.count != null ? <span className="ml-2 rounded-full bg-[#895CF6] px-1.5 text-xs text-white">{tab.count}</span> : null}
          </button>
        );
      })}
    </div>
  );
}

export function ExcelButton({ filename, headers, rows, disabled, load }: {
  filename: string;
  headers?: string[];
  rows?: ExcelValue[][];
  disabled?: boolean;
  load?: () => Promise<{ headers: string[]; rows: ExcelValue[][] }>;
}) {
  const notice = useNotice();
  const [busy, setBusy] = useState(false);
  const empty = !load && !(rows && rows.length);
  return (
    <button
      type="button"
      className="rounded-lg border border-[var(--bo-border)] px-2 py-1 text-sm text-[var(--bo-text)] disabled:opacity-40"
      disabled={disabled || busy || empty}
      onClick={() => {
        setBusy(true);
        void notice.run('Exporting Excel', async () => {
          const data = load ? await load() : { headers: headers || [], rows: rows || [] };
          await downloadWorkbook(excelFileName(filename), data.headers, data.rows);
        }, 'Excel file downloaded', { confirm: false }).finally(() => setBusy(false));
      }}
    >
      Excel
    </button>
  );
}

export function PageSizeSelect({ value, onChange }: { value: number; onChange: (size: number) => void }) {
  return (
    <label className="flex items-center gap-2 text-sm text-[var(--bo-muted)]">
      Rows
      <Select aria-label="Rows per page" className="h-8 w-20" value={String(value)} onChange={(event) => onChange(Number(event.target.value))}>
        {PAGE_SIZES.map((size) => <option key={size} value={size}>{size}</option>)}
      </Select>
    </label>
  );
}

export function SortHeader({ label, active, dir, onSort, className = 'px-4 py-3 font-medium' }: {
  label: string;
  active: boolean;
  dir: 'asc' | 'desc';
  onSort: () => void;
  className?: string;
}) {
  return (
    <th className={className}>
      <button type="button" className="inline-flex items-center gap-1 text-left" onClick={onSort}>
        {label}
        {active ? (dir === 'asc' ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />) : <ChevronsUpDown className="h-3 w-3 opacity-40" />}
      </button>
    </th>
  );
}

const OFFER_STATUS: Record<string, { tone: string; dot: string }> = {
  issued: { tone: 'bg-blue-100 text-blue-800 dark:bg-blue-900/50 dark:text-blue-200', dot: 'bg-blue-400' },
  accepted: { tone: 'bg-emerald-900 text-emerald-50', dot: 'bg-emerald-300' },
  committed: { tone: 'bg-emerald-300 text-emerald-950', dot: 'bg-emerald-600' },
};

export function StatusPill({ value, palette }: { value: string; palette?: 'offer' }) {
  const text = enumLabel(value || 'draft');
  const offer = palette === 'offer' ? OFFER_STATUS[text.toLowerCase().split(' / ')[0] || ''] : undefined;
  if (offer) {
    return <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs ${offer.tone}`}><span className={`h-1.5 w-1.5 rounded-full ${offer.dot}`} />{text}</span>;
  }
  const part = text === 'Part credited';
  const closed = /closed|void|cancelled|expired/i.test(text);
  const live = /active|paid|issued|approved|accepted|committed/i.test(text);
  const tone = part
    ? 'bg-amber-100 text-amber-900 dark:bg-amber-900/40 dark:text-amber-200'
    : closed ? 'bg-[var(--bo-pill)] text-[var(--bo-muted)]' : live ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/50 dark:text-emerald-300' : 'bg-[var(--bo-pill)] text-[var(--bo-muted)]';
  return <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs ${tone}`}><span className={`h-1.5 w-1.5 rounded-full ${part ? 'bg-amber-500' : live ? 'bg-emerald-400' : 'bg-[var(--bo-faint)]'}`} />{text}</span>;
}

export function ContextPanel({ label, title, titleAction, subtitle, status, onClose, children, footer }: {
  label: string;
  title: string;
  titleAction?: ReactNode;
  subtitle?: string;
  status?: string;
  onClose: () => void;
  children: ReactNode;
  footer?: ReactNode;
}) {
  return (
    <aside className="flex h-full min-h-0 w-full min-w-0 flex-col overflow-hidden rounded-lg border border-[var(--bo-border)] bg-[var(--bo-surface)] max-md:h-auto max-md:overflow-visible">
      <div className="flex items-start justify-between gap-3 border-b border-[var(--bo-border)] px-4 py-3">
        <div className="min-w-0">
          <div className="text-[11px] font-semibold uppercase tracking-wider text-[var(--bo-faint)]">{label}</div>
          <div className="mt-1 flex flex-wrap items-center gap-3">
            <div className="break-words text-lg font-semibold md:truncate">{title}</div>
            {titleAction}
          </div>
          <div className="mt-1 flex flex-wrap items-center gap-2 text-sm text-[var(--bo-muted)]">
            {subtitle ? <span>{subtitle}</span> : null}
            {status ? <StatusPill value={status} /> : null}
          </div>
        </div>
        <button type="button" className="shrink-0 text-sm text-[var(--bo-link)] md:hidden" onClick={onClose}>Return to list</button>
        <button type="button" aria-label="Close panel" className="hidden rounded-lg p-1 text-[var(--bo-muted)] hover:bg-[var(--bo-hover)] md:inline" onClick={onClose}><X className="h-4 w-4" /></button>
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto px-4 py-4 max-md:overflow-visible">{children}</div>
      {footer ? <div className="flex items-center justify-between gap-2 border-t border-[var(--bo-border)] px-4 py-3">{footer}</div> : null}
    </aside>
  );
}

export function SectionCard({ title, hint, children }: { title: string; hint?: string; children: ReactNode }) {
  return (
    <section className="rounded-lg border border-[var(--bo-border)] bg-[var(--bo-surface)] p-4">
      <h2 className="text-base font-semibold">{title}</h2>
      {hint ? <p className="mb-3 mt-1 text-sm text-[var(--bo-muted)]">{hint}</p> : <div className="mb-3" />}
      {children}
    </section>
  );
}
